import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

/** Series configuration for bar charts */
export interface BarSeries {
  dataKey: string;
  name: string;
  color: string;
  stackId?: string;
  opacity?: number;
}

export interface BarChartWrapperProps {
  /** Chart data array */
  data: Record<string, unknown>[];
  /** Category axis data key */
  categoryKey: string;
  /** Bar series definitions */
  series: BarSeries[];
  /** Chart height in pixels */
  height?: number;
  /** Use horizontal layout (bars go left-to-right) */
  horizontal?: boolean;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Custom tooltip formatter */
  tooltipFormatter?: (value: ValueType | undefined, name: NameType | undefined) => [React.ReactNode, NameType] | React.ReactNode;
}

const GRID_COLOR = '#EFEFEF';
const AXIS_TICK_COLOR = '#555555';

/**
 * Recharts BarChart wrapper with standard OUTFRONT styling.
 * Supports vertical (default) and horizontal layouts, stacked bars.
 */
export function BarChartWrapper({
  data,
  categoryKey,
  series,
  height = 300,
  horizontal = false,
  yAxisLabel,
  tooltipFormatter,
}: BarChartWrapperProps) {
  const layout = horizontal ? 'vertical' : 'horizontal';

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={layout} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={!horizontal} vertical={horizontal} />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
              tickLine={false}
              axisLine={{ stroke: GRID_COLOR }}
            />
            <YAxis
              type="category"
              dataKey={categoryKey}
              tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
              tickLine={false}
              axisLine={false}
              width={120}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey={categoryKey}
              tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
              tickLine={false}
              axisLine={{ stroke: GRID_COLOR }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
              tickLine={false}
              axisLine={false}
              label={
                yAxisLabel
                  ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 12, fill: AXIS_TICK_COLOR }
                  : undefined
              }
            />
          </>
        )}
        <Tooltip
          contentStyle={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #DCDCDC',
            borderRadius: 4,
            fontSize: 13,
          }}
          formatter={tooltipFormatter}
        />
        <Legend
          verticalAlign="top"
          iconType="circle"
          wrapperStyle={{ paddingBottom: 16, fontSize: 11, fontWeight: 600 }}
        />
        {series.map((s) => (
          <Bar
            key={s.dataKey}
            dataKey={s.dataKey}
            name={s.name}
            fill={s.color}
            fillOpacity={s.opacity ?? 0.75}
            stroke={s.color}
            strokeWidth={1}
            stackId={s.stackId}
            radius={s.stackId ? undefined : [2, 2, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default BarChartWrapper;
